import { useMemo } from 'react';
import { useFeatureEntitlement } from '../useFeatureEntitlement';
import { parseRollout, resolveAccess, PipelineTrackerAccess } from './accessRules';

/**
 * Does this tenant hold the Pipeline Tracker, and is it switched on?
 *
 * WHY THIS EXISTS. Both nav surfaces gate on `grantedFeatures`, which AppLayout
 * feeds from `companyData.featureFlags`. The tracker is granted as an
 * `entitlements/config.addOnFeatures.pipeline_tracker` record with grantSource
 * 'contract', and it must NEVER also be written to `featureFlags`, because
 * holding two grant records makes revocation ambiguous and an explicit `false`
 * there beats every grant.
 *
 * Without this hook the route would be reachable by direct URL while the menu
 * entry stayed invisible: the tenant would own a feature they could not find.
 * The answer therefore comes from the server entitlement SSOT via
 * `checkFeatureEntitlement`, which reads addOnFeatures directly and fails
 * closed on error, the right default for a paid feature.
 */
export function usePipelineTrackerAccess(
  companyId: string | undefined,
  featureFlags: Record<string, unknown> | undefined
): PipelineTrackerAccess {
  const { hasFeature, loading } = useFeatureEntitlement(companyId, 'pipeline_tracker');
  const rollout = parseRollout(featureFlags?.pipeline_tracker_rollout);
  return useMemo(
    () => resolveAccess(hasFeature, rollout, loading),
    [hasFeature, rollout, loading]
  );
}

export type { PipelineTrackerAccess, TrackerRollout } from './accessRules';
